import * as React from "react";
import { Check, ChevronDown, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Show the search box once the list is long enough to be worth filtering.
const SEARCH_THRESHOLD = 8;

/**
 * MultiSelect — a popover with checkbox options for filtering by multiple values.
 * When the option list is long (> SEARCH_THRESHOLD), a search box is shown to
 * filter the options by label.
 *
 * Props:
 *   options: Array<{ value: string, label: string }>
 *   selected: string[]                     // currently selected values
 *   onChange: (next: string[]) => void
 *   placeholder: string                    // shown when nothing selected (e.g. "All Stages")
 *   className: string                      // applied to the trigger button
 */
export function MultiSelect({ options, selected = [], onChange, placeholder = "All", className, ...props }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  // Reset the query whenever the popover closes so it opens fresh next time.
  React.useEffect(() => { if (!open) setQuery(""); }, [open]);

  const toggle = (value) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const count = selected.length;
  const label =
    count === 0 ? placeholder
    : count === 1 ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
    : `${placeholder} (${count})`;

  const showSearch = options.length > SEARCH_THRESHOLD;
  const q = query.trim().toLowerCase();
  const visible = q
    ? options.filter((o) => (o.label || "").toLowerCase().includes(q) || (o.value || "").toLowerCase().includes(q))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-10 items-center justify-between gap-1 rounded-md border border-input bg-white px-3 text-sm font-normal ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            className
          )}
          {...props}
        >
          <span className={cn("truncate", count === 0 && "text-muted-foreground")}>{label}</span>
          {count > 0 ? (
            <X className="h-3.5 w-3.5 shrink-0 opacity-60 hover:opacity-100" onClick={clear} />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        {showSearch && (
          <div className="relative mb-1">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full rounded border border-input bg-white pl-7 pr-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        )}
        <div className="max-h-64 overflow-y-auto">
          {options.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">No options</p>
          ) : visible.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">No matches</p>
          ) : (
            visible.map((o) => {
              const checked = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                      checked ? "border-navy bg-navy text-white" : "border-navy/30 bg-white"
                    )}
                  >
                    {checked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
